// Fatia S4: Perfil para a IA (produtos, diferenciais, tom de voz...), preencher a partir do site, memórias e créditos.
import { useEffect, useState } from 'react'
import { Globe, Plus, RefreshCw, Save, Star, Trash2 } from 'lucide-react'
import { dateBR } from '@impulso/shared'
import type { BusinessProfileDto, ProfileExtractResponse } from '@impulso/shared/api/ai'
import { useCan, useCompany } from '../../lib/company'
import { errorMessage } from '../../lib/api'
import { ErrorState, Loading, PageHead } from '../../ui'
import { useToast } from '../../ui/toast'
import CompanySettingsTabs from '../../features/account/CompanySettingsTabs'
import { emptyProfile, useExtractProfile, useProfile, useSaveProfile } from '../../features/context/api'
import CreditsCard from '../../features/context/CreditsCard'
import ExtractReview from '../../features/context/ExtractReview'
import FactsCard from '../../features/context/FactsCard'
import TagInput from '../../features/context/TagInput'

const norm = (s: string) => s.trim().toLowerCase()

function merge(form: BusinessProfileDto, patch: Partial<BusinessProfileDto>): BusinessProfileDto {
  const out = { ...form, ...patch }
  if (patch.products) out.products = [...form.products, ...patch.products.filter((p) => !form.products.some((x) => norm(x.name) === norm(p.name)))].slice(0, 30)
  if (patch.differentials) out.differentials = [...form.differentials, ...patch.differentials.filter((d) => !form.differentials.some((x) => norm(x) === norm(d)))].slice(0, 10)
  return out
}

export default function SettingsAiProfile() {
  const company = useCompany()
  const canEdit = useCan('company.edit_settings')
  const canEditFacts = useCan('company.edit_drafts')
  const q = useProfile(company.id)
  const save = useSaveProfile(company.id)
  const extract = useExtractProfile(company.id)
  const toast = useToast()
  const [form, setForm] = useState<BusinessProfileDto>(emptyProfile())
  const [dirty, setDirty] = useState(false)
  const [fromExtraction, setFromExtraction] = useState(false)
  const [review, setReview] = useState<ProfileExtractResponse | null>(null)

  useEffect(() => {
    if (q.data && !dirty) setForm(q.data.profile)
  }, [q.data, dirty])

  const set = (patch: Partial<BusinessProfileDto>) => {
    setForm((f) => ({ ...f, ...patch }))
    setDirty(true)
  }

  const onSave = () =>
    save.mutate(
      { ...form, products: form.products.filter((p) => p.name.trim()), fromExtraction },
      {
        onSuccess: () => {
          setDirty(false)
          setFromExtraction(false)
          toast('Perfil salvo. A IA vai usar essas informações a partir de agora.')
        },
      },
    )

  const runExtract = () =>
    extract.mutate({ website: form.website || null, instagram: form.instagram || null }, { onSuccess: (r) => setReview(r) })

  const products = form.products
  const setProduct = (i: number, patch: Partial<BusinessProfileDto['products'][number]>) => set({ products: products.map((p, j) => (j === i ? { ...p, ...patch } : p)) })

  return (
    <>
      <PageHead title="Configurações" sub="Quanto mais a IA souber do seu negócio, melhores os textos e as ideias." />
      <CompanySettingsTabs />
      {q.isLoading && <Loading lines={6} />}
      {q.error && <ErrorState error={q.error} onRetry={() => q.refetch()} />}
      {q.data && (
        <div className="stack" style={{ gap: 20 }}>
          <div className="row wrap" style={{ gap: 8 }}>
            {q.data.confirmedAt ? <span className="badge green">Perfil confirmado em {dateBR(q.data.confirmedAt)}</span> : <span className="badge amber">Perfil ainda não confirmado</span>}
            {!canEdit && <span className="badge">Somente leitura</span>}
          </div>

          {canEdit && (
            <section className="card stack" style={{ gap: 12 }}>
              <div className="card-head" style={{ marginBottom: 0 }}>
                <span className="ic blue"><Globe size={18} /></span>
                <div>
                  <h3>Preencher a partir do site ou Instagram</h3>
                  <p className="small muted">A gente lê a página pública e sugere produtos, diferenciais e tom de voz. Você revisa antes de salvar.</p>
                </div>
              </div>
              <div className="grid g-2">
                <div className="field">
                  <label htmlFor="ai-site">Site</label>
                  <input id="ai-site" className="input" placeholder="www.seunegocio.com.br" value={form.website ?? ''} maxLength={200} onChange={(e) => set({ website: e.target.value || null })} />
                </div>
                <div className="field">
                  <label htmlFor="ai-ig">Instagram</label>
                  <input id="ai-ig" className="input" placeholder="@seunegocio" value={form.instagram ?? ''} maxLength={80} onChange={(e) => set({ instagram: e.target.value || null })} />
                </div>
              </div>
              <button type="button" className="btn" style={{ alignSelf: 'flex-start' }} onClick={runExtract} disabled={extract.isPending || (!form.website && !form.instagram)}>
                {extract.isPending ? <RefreshCw size={16} className="spin" /> : <Globe size={16} />} {extract.isPending ? 'Lendo a página…' : 'Preencher a partir do site'}
              </button>
              {extract.error && <div className="form-error" role="alert">{errorMessage(extract.error)}</div>}
              {review && (
                <ExtractReview
                  result={review}
                  onDiscard={() => setReview(null)}
                  onApply={(patch) => {
                    setForm((f) => merge(f, patch))
                    setDirty(true)
                    setFromExtraction(true)
                    setReview(null)
                    toast('Sugestões aplicadas. Revise e clique em “Salvar perfil”.')
                  }}
                />
              )}
            </section>
          )}

          <section className="card stack" style={{ gap: 16 }}>
            <h3>Seu negócio para a IA</h3>

            <div className="field">
              <label>Produtos e serviços (com preço “a partir de”)</label>
              {products.length === 0 && <p className="small muted">Nenhum produto ainda.</p>}
              {products.map((p, i) => (
                <div key={i} className="row wrap" style={{ gap: 8 }}>
                  <input className="input" style={{ flex: '2 1 180px', minWidth: 0 }} placeholder="Ex.: Bolo de aniversário" value={p.name} maxLength={80} disabled={!canEdit} aria-label="Nome do produto" onChange={(e) => setProduct(i, { name: e.target.value })} />
                  <input className="input" style={{ flex: '1 1 100px', minWidth: 0 }} type="number" min={0} step="0.01" inputMode="decimal" placeholder="R$ a partir de" value={p.priceFrom ?? ''} disabled={!canEdit} aria-label="Preço a partir de" onChange={(e) => setProduct(i, { priceFrom: e.target.value === '' ? null : Number(e.target.value) })} />
                  <button type="button" className={`icon-btn ${p.highlight ? 'on' : ''}`} title="Destaque" aria-pressed={!!p.highlight} aria-label="Marcar como destaque" disabled={!canEdit} onClick={() => setProduct(i, { highlight: !p.highlight })}>
                    <Star size={16} fill={p.highlight ? 'var(--amber)' : 'none'} color={p.highlight ? 'var(--amber)' : undefined} />
                  </button>
                  {canEdit && <button type="button" className="icon-btn" aria-label="Remover produto" onClick={() => set({ products: products.filter((_, j) => j !== i) })}><Trash2 size={16} /></button>}
                </div>
              ))}
              {canEdit && products.length < 30 && (
                <button type="button" className="btn sm" style={{ alignSelf: 'flex-start' }} onClick={() => set({ products: [...products, { name: '', priceFrom: null }] })}><Plus size={14} /> Adicionar produto</button>
              )}
            </div>

            <div className="field">
              <label>Diferenciais</label>
              <TagInput label="Diferenciais" value={form.differentials} onChange={(v) => set({ differentials: v })} placeholder="Ex.: feito no dia, entrega em BH" disabled={!canEdit} />
            </div>

            <div className="grid g-2">
              <div className="field">
                <label htmlFor="ai-tone">Tom de voz</label>
                <input id="ai-tone" className="input" placeholder="Ex.: próximo, carinhoso, sem exageros" value={form.toneOfVoice ?? ''} maxLength={200} disabled={!canEdit} onChange={(e) => set({ toneOfVoice: e.target.value || null })} />
              </div>
              <div className="field">
                <label htmlFor="ai-season">Datas importantes do negócio</label>
                <input id="ai-season" className="input" placeholder="Ex.: Páscoa e Dia das Mães vendem muito" value={form.seasonality ?? ''} maxLength={300} disabled={!canEdit} onChange={(e) => set({ seasonality: e.target.value || null })} />
              </div>
            </div>

            <div className="field">
              <label htmlFor="ai-customer">Cliente ideal</label>
              <textarea id="ai-customer" className="textarea" placeholder="Ex.: mães e pais de 28 a 45 anos organizando festas" value={form.idealCustomer ?? ''} maxLength={300} disabled={!canEdit} onChange={(e) => set({ idealCustomer: e.target.value || null })} />
            </div>

            <div className="field">
              <label>O que a IA nunca deve dizer</label>
              <TagInput label="Nunca dizer" value={form.neverSay} onChange={(v) => set({ neverSay: v })} placeholder="Ex.: o melhor de BH, preço mais baixo" max={30} maxLength={80} disabled={!canEdit} />
            </div>

            <div className="grid g-2">
              <div className="field">
                <label>Faz entrega?</label>
                <div className="row wrap" style={{ gap: 6 }}>
                  {([[true, 'Sim'], [false, 'Não'], [null, 'Não informar']] as const).map(([v, label]) => (
                    <button key={label} type="button" className={`chip ${form.delivers === v ? 'on' : ''}`} disabled={!canEdit} aria-pressed={form.delivers === v} onClick={() => set({ delivers: v })}>{label}</button>
                  ))}
                </div>
              </div>
              <div className="field">
                <label htmlFor="ai-radius">Raio de atendimento (km)</label>
                <input id="ai-radius" className="input" type="number" min={0} max={1000} inputMode="numeric" placeholder="Ex.: 8" value={form.serviceRadiusKm ?? ''} disabled={!canEdit} onChange={(e) => set({ serviceRadiusKm: e.target.value === '' ? null : Number(e.target.value) })} />
              </div>
            </div>

            {save.error && <div className="form-error" role="alert">{errorMessage(save.error)}</div>}
            {canEdit && (
              <div className="form-actions">
                {dirty && <span className="small muted">Alterações ainda não salvas.</span>}
                <button type="button" className="btn primary" onClick={onSave} disabled={save.isPending}>
                  {save.isPending ? <RefreshCw size={16} className="spin" /> : <Save size={16} />} Salvar perfil
                </button>
              </div>
            )}
          </section>

          <div className="grid g-2" style={{ alignItems: 'start' }}>
            <FactsCard companyId={company.id} canEdit={canEditFacts} />
            <CreditsCard companyId={company.id} />
          </div>
        </div>
      )}
    </>
  )
}
